import { getGoalParentIds } from '@griply/utils';
import { useCallback, useMemo } from 'react';
import { TreeItemMove } from 'shared/components/ui/virtualized-tree-list';
import { useEntityLinks } from 'shared/hooks/use-entity-links';
import { GoalParentLinkChange } from 'shared/types/goal';
import { ID } from 'shared/types/id';

import { useGoals } from './use-goals';

// A goal rendered under several parents moves only the link of the row that was dragged.
// `rootParentId` is the goal a root row hangs under — goal-detail's list roots are its own children.
export const useResolveGoalParentChanges = () => {
  const goals = useGoals();
  const links = useEntityLinks();

  const goalsById = useMemo(
    () => new Map(goals.map((goal) => [goal.id, goal])),
    [goals],
  );

  return useCallback(
    (
      moves: TreeItemMove[],
      rootIds: readonly ID[],
      rootParentId?: ID,
    ): GoalParentLinkChange[] =>
      moves.reduce<GoalParentLinkChange[]>(
        (acc, { itemId, fromParentId, toParentId }) => {
          const movedGoal = goalsById.get(itemId);
          if (!movedGoal || toParentId === fromParentId) {
            return acc;
          }

          const sourceId =
            !fromParentId || rootIds.includes(fromParentId)
              ? rootParentId
              : fromParentId;
          const remaining = getGoalParentIds(movedGoal, { links }).filter(
            (id) => id !== sourceId,
          );
          const targetId = rootIds.includes(toParentId)
            ? rootParentId
            : toParentId;

          acc.push({
            goalId: itemId,
            parentIds: targetId
              ? Array.from(new Set([...remaining, targetId]))
              : remaining,
          });
          return acc;
        },
        [],
      ),
    [goalsById, links],
  );
};
